import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, Alert, Loader, Badge, Select, Dialog } from '@dreamtree-org/twreact-ui';
import { Boxes, Plus, Github, GitBranch, RefreshCw, Archive, Pencil } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';

const STACKS = ['MERN', 'Next.js', 'React', 'Angular', 'Vue', 'Other'];
const DEPLOY_TYPES = ['Docker', 'Dokploy', 'Kubernetes', 'NPM', 'Other'];

const errText = (ex, fallback) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || fallback;
const opts = (list) => list.map((v) => ({ value: v, label: v }));
// twreact's Select hands onChange the RAW value, not a DOM event.
const selVal = (v) => (v?.target ? v.target.value : (v?.value ?? v ?? ''));

const queueNotice = (res, ok) => {
  if (res?.enqueued) return ok;
  const why = res?.error ? ` (${res.error})` : '';
  return `Recorded, but the job queue is unreachable${why} — is Redis running?`;
};

/**
 * Development › Git Apps.
 *
 * Registers a GitHub repo as an app this workspace can browse, edit and deploy. Creating
 * one enqueues an initial repo ingest server-side (GitApp.afterCreate).
 */
export default function GitApps() {
  const { fetchApi } = useAppApi();
  const navigate = useNavigate();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState(null); // { app? } — present means open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi('gitApps', {
        action: 'list',
        where: { status: 'active' },
        orderBy: { column: 'id', direction: 'desc' },
        limit: 200,
      });
      setApps(Array.isArray(res) ? res : (res && res.data) || []);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load apps'));
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  const resync = async (a) => {
    setErr('');
    try {
      const res = await fetchApi('gitAppAction', 'resync', a.id);
      setNotice(queueNotice(res?.data, `Re-ingest queued for ${a.name}.`));
    } catch (ex) {
      setErr(errText(ex, 'Could not queue a re-sync'));
    }
  };

  const archive = async (a) => {
    setErr('');
    try {
      await fetchApi('gitApps', { action: 'update', where: { id: a.id }, data: { status: 'archived' } });
      setNotice(`${a.name} archived.`);
      await load();
    } catch (ex) {
      setErr(errText(ex, 'Could not archive'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Boxes size={22} className="text-primary-600" />
          <div>
            <h1 className="text-xl font-bold">Git Apps</h1>
            <p className="text-sm opacity-70">Register a GitHub repository to browse, edit and deploy it here.</p>
          </div>
        </div>
        <Can module="development" action="create">
          <Button leftIcon={<Plus size={16} />} onClick={() => setDialog({})}>New app</Button>
        </Can>
      </div>

      {err ? <Alert variant="error" title="Git Apps">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Git Apps">{notice}</Alert> : null}

      {loading && <Loader text="Loading apps…" />}
      {!loading && apps.length === 0 && (
        <Card className="p-8 text-center text-sm opacity-70">
          No apps yet. Use <span className="font-semibold">New app</span> to register a repository.
        </Card>
      )}
      {!loading && apps.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((a) => (
            <Card key={a.id} className="flex flex-col">
              <button type="button" onClick={() => navigate(`/git-apps/${a.id}`)} className="text-left p-4 flex-1">
                <div className="text-sm font-bold truncate">{a.name}</div>
                <div className="text-xs opacity-70 mt-1 flex items-center gap-1 font-mono truncate">
                  <Github size={12} /> {a.repo}
                </div>
                <div className="text-xs opacity-70 mt-0.5 flex items-center gap-1">
                  <GitBranch size={12} /> {a.branch || 'default'}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {a.stack ? <Badge variant="secondary">{a.stack === 'Other' ? a.stack_other : a.stack}</Badge> : null}
                  {a.deploy_type ? <Badge variant="primary">{a.deploy_type === 'Other' ? a.deploy_other : a.deploy_type}</Badge> : null}
                </div>
              </button>
              <div className="border-t border-secondary-200 dark:border-secondary-700 px-3 py-2 flex items-center gap-3 text-[11px] font-semibold">
                <Can module="development" action="update">
                  <button type="button" className="flex items-center gap-1 opacity-70 hover:opacity-100" onClick={() => setDialog({ app: a })}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button type="button" className="flex items-center gap-1 text-primary-600 hover:underline" onClick={() => resync(a)}>
                    <RefreshCw size={12} /> Re-sync
                  </button>
                  <button type="button" className="flex items-center gap-1 text-error-600 hover:underline ml-auto" onClick={() => archive(a)}>
                    <Archive size={12} /> Archive
                  </button>
                </Can>
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialog ? (
        <AppDialog
          app={dialog.app}
          onClose={() => setDialog(null)}
          onSaved={(id) => {
            setDialog(null);
            load();
            if (id) navigate(`/git-apps/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Create/edit dialog. The repo and branch pickers read the caller's own GitHub grant, so
 * an unconnected user gets a pointer to /github rather than an empty list they can't explain.
 */
function AppDialog({ app = null, onClose, onSaved }) {
  const { fetchApi } = useAppApi();
  const isEdit = !!app;

  const [repos, setRepos] = useState(null);
  const [installUrl, setInstallUrl] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [repo, setRepo] = useState(app?.repo || '');
  const [branch, setBranch] = useState(app?.branch || '');
  const [name, setName] = useState(app?.name || '');
  const [stack, setStack] = useState(app?.stack || '');
  const [stackOther, setStackOther] = useState(app?.stack_other || '');
  const [deploy, setDeploy] = useState(app?.deploy_type || '');
  const [deployOther, setDeployOther] = useState(app?.deploy_other || '');

  useEffect(() => {
    let alive = true;
    fetchApi('githubListRepos')
      .then((res) => { if (alive) setRepos(res?.data?.repos || []); })
      .catch((ex) => {
        if (!alive) return;
        setRepos([]);
        setErr(`${errText(ex, 'Could not list repositories')} — connect GitHub first.`);
      })
      .finally(() => { if (alive) setLoadingRepos(false); });

    fetchApi('githubStatus')
      .then((res) => { if (alive) setInstallUrl(res?.data?.install_url || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [fetchApi]);

  const pickRepo = async (full) => {
    setRepo(full);
    setBranch('');
    setBranches([]);
    if (!full) return;
    if (!name) setName(full.split('/')[1] || full);
    const picked = (repos || []).find((r) => r.full_name === full);
    if (picked?.default_branch) setBranch(picked.default_branch);
    setLoadingBranches(true);
    try {
      const res = await fetchApi('githubListBranches', { repo: full });
      setBranches(res?.data?.branches || []);
    } catch {
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  };

  const save = async () => {
    if (!repo || !name.trim()) { setErr('Repository and name are required.'); return; }
    setBusy(true);
    setErr('');
    const data = {
      name: name.trim(),
      repo,
      branch: branch || null,
      stack: stack || null,
      stack_other: stack === 'Other' ? stackOther : null,
      deploy_type: deploy || null,
      deploy_other: deploy === 'Other' ? deployOther : null,
    };
    try {
      const res = await fetchApi('gitApps', isEdit
        ? { action: 'update', where: { id: app.id }, data }
        : { action: 'create', data });
      // korm's create comes back as [{…}] and server.js object-spreads it into {"0":{…}},
      // so the new row's id can arrive under either shape. See CLAUDE.md.
      const created = Array.isArray(res) ? res[0] : (res && (res.data?.[0] || res.data || res[0]));
      onSaved(isEdit ? app.id : (created && created.id) || null);
    } catch (ex) {
      setErr(errText(ex, 'Could not save the app'));
      setBusy(false);
    }
  };

  return (
    <Dialog isOpen onClose={onClose} title={isEdit ? `Edit ${app.name}` : 'Register a Git App'} size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {err ? <Alert variant="error" title="Git App">{err}</Alert> : null}

        {loadingRepos ? <Loader text="Loading your repositories…" /> : (
          <Select
            label="Repository"
            value={repo}
            options={(repos || []).map((r) => ({ value: r.full_name, label: r.private ? `${r.full_name} (private)` : r.full_name }))}
            onChange={(v) => pickRepo(selVal(v))}
            placeholder={repos?.length ? 'Pick a repository' : 'No repositories available'}
            searchable
          />
        )}

        {/* A user grant cannot see org-owned private repos; only an App installation can. */}
        {installUrl ? (
          <p className="text-[11px] opacity-60">
            Repository missing?{' '}
            <a className="text-primary-600 hover:underline" href={installUrl} target="_blank" rel="noreferrer">
              Install the GitHub App
            </a>{' '}on that organisation.
          </p>
        ) : null}

        <Select
          label="Branch"
          value={branch}
          options={branches.map((b) => ({ value: b.name, label: b.protected ? `${b.name} (protected)` : b.name }))}
          onChange={(v) => setBranch(selVal(v))}
          placeholder={loadingBranches ? 'Loading branches…' : 'Repository default'}
        />

        <Input label="App name" value={name} onChange={(e) => setName(e.target.value)} required />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select label="Stack" value={stack} options={opts(STACKS)} onChange={(v) => setStack(selVal(v))} placeholder="Not set" />
          {stack === 'Other' ? (
            <Input label="Stack (other)" value={stackOther} onChange={(e) => setStackOther(e.target.value)} />
          ) : null}
          <Select label="Deploy type" value={deploy} options={opts(DEPLOY_TYPES)} onChange={(v) => setDeploy(selVal(v))} placeholder="Not set" />
          {deploy === 'Other' ? (
            <Input label="Deploy type (other)" value={deployOther} onChange={(e) => setDeployOther(e.target.value)} />
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!repo || !name.trim()}>
            {isEdit ? 'Save' : 'Register'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
